import { Navigate, Outlet } from "react-router";
import { useIntl } from "react-intl";

import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useReleaseAccessGate } from "@/hooks/use-release-access-gate";
import { CHEETAH_LOGO_SRC } from "@/lib/brand-assets";
import { getUserInitials } from "@/lib/user";
import { LocaleSwitcher } from "@/components/locale-switcher";
import { Avatar, AvatarFallback, AvatarImage } from "@runcheetah/ui";

/**
 * Minimal auth-guard layout — no sidebar, no nav chrome.
 * Used for standalone flows like The Origin and Processing.
 * Just a slim header with logo + avatar.
 */
export default function ProtectedLayout({
  releaseAccessRequired = true,
}: {
  releaseAccessRequired?: boolean;
}) {
  const intl = useIntl();
  const { user, isPending } = useAuthenticatedUser();
  const releaseAccessGate = useReleaseAccessGate({
    enabled: Boolean(user) && releaseAccessRequired,
    accessRequired: releaseAccessRequired,
  });

  if (isPending || releaseAccessGate.isPending) {
    return <div className="min-h-screen bg-background" />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (releaseAccessGate.redirectTo) {
    return <Navigate to={releaseAccessGate.redirectTo} replace />;
  }

  const initials = getUserInitials(user);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Minimal header */}
      <header className="flex h-14 shrink-0 items-center justify-between px-6">
        <img
          src={CHEETAH_LOGO_SRC}
          alt={intl.formatMessage({ id: "common.brandName", defaultMessage: "Cheetah" })}
          className="h-7 w-auto"
        />
        <div className="flex items-center gap-3">
          <LocaleSwitcher className="rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground" />
          <Avatar className="h-8 w-8">
            {user.image ? (
              <AvatarImage src={user.image} alt={user.name} referrerPolicy="no-referrer" />
            ) : null}
            <AvatarFallback className="bg-peach text-peach-foreground text-xs font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
        </div>
      </header>

      <main className="flex flex-1 flex-col">
        <Outlet />
      </main>
    </div>
  );
}
